import { Controller } from '@hotwired/stimulus'

/**
 * @file Stimulus-контроллер клиентской валидации формы перед сабмитом.
 *
 * Вешается на `form` с `novalidate`: по сабмиту вызывает `validate()` у всех
 * вложенных контроллеров полей (input, checkbox и т.п.), показывая
 * стилизованные ошибки компонентов вместо нативных тултипов браузера.
 * Невалидная форма не отправляется, фокус переводится на первое поле
 * с ошибкой.
 *
 * @example
 *   = form_tag url, novalidate: true,
 *       data: { controller: 'form-validation',
 *               action: 'submit->form-validation#submit' }
 */
export default class FormValidationController extends Controller {
  /**
   * Валидирует форму и блокирует отправку при ошибках.
   *
   * @param {SubmitEvent} event - Событие отправки формы.
   * @returns {void}
   */
  submit(event) {
    // Каждое поле валидируется независимо, чтобы показать все ошибки разом.
    const invalid = this.#validatableControllers().filter((controller) => {
      const result = controller.validate()

      return (
        result === false || controller.element.classList.contains('is-invalid')
      )
    })

    if (invalid.length === 0) return

    event.preventDefault()
    this.#focusFirstInvalidField()
  }

  /**
   * Находит вложенные контроллеры, которые умеют валидировать своё состояние.
   *
   * @returns {Controller[]} Список контроллеров с методом `validate()`.
   */
  #validatableControllers() {
    return Array.from(
      this.element.querySelectorAll('[data-controller]')
    ).flatMap((element) =>
      element.dataset.controller
        .split(/\s+/)
        .map((identifier) =>
          this.application.getControllerForElementAndIdentifier(
            element,
            identifier
          )
        )
        .filter((controller) => controller?.validate)
    )
  }

  /**
   * Прокручивает форму к первому невалидному элементу и переводит фокус
   * на связанное поле.
   *
   * @returns {void}
   */
  #focusFirstInvalidField() {
    const invalidElement = this.element.querySelector(
      '.is-invalid, input:invalid, select:invalid, textarea:invalid'
    )

    if (!invalidElement) return

    invalidElement.scrollIntoView({ behavior: 'smooth', block: 'center' })

    const field = invalidElement.matches('input, select, textarea')
      ? invalidElement
      : invalidElement.querySelector('input, select, textarea, button')

    field?.focus({ preventScroll: true })
  }
}
